"use client";

import { useState, useEffect, useContext, useRef, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import axios from 'axios';
import { Info, Video, Star, Minus, Plus, Maximize2, Mail, X, Loader, Check, Download, Image as ImageIcon, Layers, ChevronLeft, ChevronRight, Heart } from "lucide-react";
// Importation des composants Swiper et des styles nécessaires
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from 'swiper/modules';
import VideoStory from "./VideoStory";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { FaWhatsapp } from "react-icons/fa";
import { ShoppingCart as LucideShoppingCart } from 'lucide-react';
import ProductStickyBar from "./ProductStickyBar";
import ProductGallery from "./ProductGallery";
import { CartContext } from "../shopping/CartContext";
import { useWishlist } from "../wishlist/WishlistContext";
import {API_UPLOAD_URL, API_BASE_URL } from '@/app/lib/api';
import ProductCard from "../produit/product_card";
import ReviewsSection from "./ReviewsSection";
// Video story component removed — videos are no longer rendered in the gallery
// --- Définition des Types (INCHANGÉ) ---
export interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  category: { id: string; name: string; slug: string; colorHome?: string; colorCataloge?: string };
  // optional category color hints (may be provided by the API)
  // colorHome: background swatch used in some UIs
  // colorCataloge: accent used for catalogue / card shadow
  // these are optional because older API responses may not include them
  images: string[];
  description?: string;
  about_product?: string[];
  promoPrice?: number | null;
  stock?: number;
  sku?: string;
  createdAt?: string;
  videoUrl?: string | null;
  ficheTechnique?: string | null;
}
export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
}

// Review type to improve typing in the reviews section
interface Review {
  id?: string | number;
  note?: number;
  rating?: number;
  nom?: string;
  name?: string;
  nom_utilisateur?: string;
  commentaire?: string;
  description?: string;
  texte?: string;
  createdAt?: string;
  created_at?: string;
}



interface ProductClientViewProps {
  product: Product;
  similarProducts: Product[];
}

// --- Fonction Utilitaire (INCHANGÉ) ---
// isNewProduct removed (not used)

// ==================================================================
//          COMPOSANTS & DONNÉES POUR LA STICKY BAR (INCHANGÉ)
// ==================================================================
const navLinks = [
  { id: 'overview', label: 'Aperçu', icon: Info },
  { id: 'Vediolink--anchor', label: 'Video', icon: Video },
  { id: 'review_section', label: 'Reviews', icon: Star },
  { id: 'similair', label: 'Similaires', icon: Layers },
];


// ==================================================================
//                     COMPOSANT PRINCIPAL (INCHANGÉ)
// ==================================================================
export default function ProductClientView({ product, similarProducts }: ProductClientViewProps) {
  const cartContext = useContext(CartContext);
  // Lightbox pour images produit
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  if (!cartContext) {
    throw new Error("Ce composant doit être utilisé à l'intérieur d'un CartProvider.");
  }
  const { cartItems, addToCart, openCart } = cartContext;

  // Wishlist
  const { toggle: toggleWish, isWished } = useWishlist();

  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showBar, setShowBar] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  // Gallery: scroller ref and selected thumbnail state
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  // touch handling for swipe on main viewer
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef(0);
  // track whether thumbnail scroller can scroll left/right (used internally)
  const canScrollUpRef = useRef(false);
  const canScrollDownRef = useRef(false);

  const handleThumbnailClick = (idx: number) => {
    setSelectedIndex(idx);
    // keep lightboxIndex in sync so expand opens correct slide
    setLightboxIndex(idx);
  };

  // scrollThumbnails removed (not used) - keep scrollerRef for thumbnail scrollIntoView in goToIndex

  // Helper to programmatically go to an index and ensure the thumbnail is visible
  const goToIndex = (idx: number) => {
    const len = mediaItems.length;
    if (len === 0) return;
    const normalized = ((idx % len) + len) % len;
    setSelectedIndex(normalized);
    setLightboxIndex(normalized);
    // scroll thumbnail into view if possible
    const el = scrollerRef.current;
    if (el) {
      const thumb = el.querySelector(`[data-gallery-index="${normalized}"]`) as HTMLElement | null;
      if (thumb) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  const prevImage = () => goToIndex(selectedIndex - 1);
  const nextImage = () => goToIndex(selectedIndex + 1);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const update = () => {
      canScrollUpRef.current = el.scrollTop > 0;
      canScrollDownRef.current = el.scrollHeight > el.clientHeight + el.scrollTop + 1;
    };

    // Run immediately and again after short delays to account for image loading and layout shifts
    update();
    const t1 = setTimeout(update, 120);
    const t2 = setTimeout(update, 500);

    // Recompute when scrolling or resizing
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    // If thumbnails include images that are still loading, listen to their load events
    const imgs: HTMLImageElement[] = Array.from(el.querySelectorAll('img')) as HTMLImageElement[];
    const onImgLoad = () => update();
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener('load', onImgLoad);
    });

    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      clearTimeout(t1);
      clearTimeout(t2);
      imgs.forEach((img) => img.removeEventListener('load', onImgLoad));
    };
  }, [product.images?.length]);

  useEffect(() => {
    // if lightboxIndex changed (e.g. via mobile thumbnails or lightbox), keep desktop selected consistent
    setSelectedIndex(lightboxIndex ?? 0);
  }, [lightboxIndex]);

  // Ajout pour la gestion des avis
  const [isReviewModalOpen, setReviewModalOpen] = useState(false);
  const [formName, setFormName] = useState("");
  const [formComment, setFormComment] = useState("");
  const [formImage, setFormImage] = useState<File | null>(null);
  const [formRating, setFormRating] = useState(0);
  const [formHoverRating, setFormHoverRating] = useState(0);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [alert, setAlert] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const ratingLabels = ["", "Décevant", "Moyen", "Bon", "Très bon", "Excellent"];

  // Stock notification (back-in-stock) modal state
  const [isNotifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyProductId, setNotifyProductId] = useState<string | null>(null);
  const [notifyProductName, setNotifyProductName] = useState<string | null>(null);
  const [isSubmittingNotify, setIsSubmittingNotify] = useState(false);
  const [notifyAlert, setNotifyAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const openNotifyModal = (productId: string, name?: string) => {
    setNotifyProductId(productId);
    setNotifyProductName(name ?? null);
    setNotifyEmail('');
    setNotifyAlert(null);
    setNotifyModalOpen(true);
  };

  const submitNotify = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!notifyProductId || !notifyEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(notifyEmail)) {
      setNotifyAlert({ type: 'error', message: 'Veuillez saisir une adresse email valide.' });
      return;
    }
    setIsSubmittingNotify(true);
    try {
      await axios.post(`${API_BASE_URL}/stock-subscriptions`, { productId: notifyProductId, email: notifyEmail });
      setNotifyAlert({ type: 'success', message: 'Merci — vous serez informé(e) par email lorsque le produit sera de nouveau en stock.' });
      setTimeout(() => setNotifyModalOpen(false), 1400);
    } catch (err: unknown) {
      const maybe = err as { response?: { data?: { message?: string } }; message?: string };
      const msg = maybe?.response?.data?.message || maybe?.message || 'Une erreur est survenue. Réessayez plus tard.';
      setNotifyAlert({ type: 'error', message: String(msg) });
    } finally {
      setIsSubmittingNotify(false);
    }
  };
  // safely read product.reviews if present
  const rawReviews = (product as unknown as Record<string, unknown>)['reviews'];
  const reviews: Review[] = Array.isArray(rawReviews) ? (rawReviews as Review[]) : [];
  const totalReviews = reviews.length;
  const averageRating = totalReviews > 0 ? reviews.reduce((acc: number, review: Review) => acc + Number(review.rating || review.note || 0), 0) / totalReviews : 0;

  // Nouvel état pour les avis validés du produit
  const [shortReviews, setShortReviews] = useState<{ reviews: Review[]; count: number } | null>(null);
  const [loadingShortReviews, setLoadingShortReviews] = useState(false);

  const isOutOfStock = product.stock === 0;
  const stockQuantity = product.stock ?? 99;
  const displayPrice = product.promoPrice != null && product.promoPrice < product.price ? product.promoPrice : product.price;
  const hasDiscount = product.promoPrice != null && product.promoPrice < product.price;

  // JSON-LD Product schema for SEO (renders client-side)
  const productSchema = useMemo(() => {
    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: Array.isArray(product.images) ? product.images : (product.images ? [product.images] : []),
      description: product.description ? String(product.description).slice(0, 300) : undefined,
      sku: product.sku || undefined,
      brand: product.category?.name ? { "@type": "Brand", name: product.category.name } : undefined,
      offers: {
        "@type": "Offer",
        price: Number(displayPrice ?? product.price).toFixed(3),
        priceCurrency: "TND",
        availability: product.stock === 0 ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      },
    } as Record<string, unknown>;

    // Add aggregateRating if available
    if (typeof averageRating === 'number' && averageRating > 0) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: Number(averageRating).toFixed(1),
        reviewCount: totalReviews,
      };
    }

    return schema;
  }, [product, displayPrice, averageRating, totalReviews]);

  // deriveParentCategoryColorHome helper removed — it was unused.

  // derive some helpful values for styling (after helpers are declared)
  // NOTE: `productParentColor` removed — it was unused and caused a lint warning.

  // Compute the containing category name for the main product (used in the UI)
  // (value available if needed)

  // Prepare gallery media items: images only (videos removed)
  const images = Array.isArray(product.images) ? product.images : [];
  const mediaItems: { type: 'image'; src: string }[] = images.map(img => ({ type: 'image' as const, src: img }));

  useEffect(() => {
    const handleScroll = () => {
      setShowBar(window.scrollY > 200);
      let currentSectionId = 'overview';
      for (const link of navLinks) {
        const element = document.getElementById(`${link.id}`);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 250) {
            currentSectionId = link.id;
          }
        }
      }
      setActiveSection(currentSectionId);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Récupérer les avis validés pour ce produit
    const fetchShortReviews = async () => {
      setLoadingShortReviews(true);
      try {
        const apiUrl = `${API_BASE_URL}/reviews/product/${product.id}`;
        const res = await axios.get(apiUrl);
        setShortReviews(res.data);
      } catch {
        setShortReviews({ reviews: [], count: 0 });
      } finally {
        setLoadingShortReviews(false);
      }
    };
    fetchShortReviews();
  }, [product.id]);

  useEffect(() => {
    // Meta Pixel: ViewContent
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'ViewContent', {
        content_name: product.name,
        content_category: product.category?.name,
        content_ids: [product.id],
        content_type: 'product',
        value: displayPrice,
        currency: 'TND'
      });
    }
  }, [product, displayPrice]);

  const handleAddToCart = () => {
    if (isOutOfStock || isAddingToCart) return;
    setIsAddingToCart(true);
    // try to derive a child category name from several possible API shapes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const prodAny = product as any;
    const childCategoryName: string | undefined = prodAny?.childCategoryName ?? prodAny?.parentCategoryName ?? prodAny?.category?.name ?? (Array.isArray(prodAny?.productCategories) ? prodAny.productCategories[0]?.category?.name : undefined);

    addToCart({
      id: product.id,
      name: product.name,
      price: displayPrice,
      image: product.images?.[0] || '/logo.png.webp',
      quantity: quantity,
      slug: product.slug,
      parentCategoryName: childCategoryName ?? product.category?.name ?? undefined,
    });

    // Meta Pixel: AddToCart
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'AddToCart', {
        content_name: product.name,
        content_category: childCategoryName ?? product.category?.name,
        content_ids: [product.id],
        content_type: 'product',
        value: displayPrice,
        currency: 'TND'
      });
    }

    openCart();
    setTimeout(() => setIsAddingToCart(false), 500);
  };

  const handleQuantityChange = (action: 'increase' | 'decrease') => {
    if (action === 'increase') setQuantity(prev => Math.min(prev + 1, stockQuantity));
    else if (action === 'decrease') setQuantity(prev => Math.max(1, prev - 1));
  };

  const formatPrice = (p: number) => `${p.toFixed(3)} DT`;

  // Helper: produce a URL-friendly slug from a category name when a slug isn't provided by the API
  const slugify = (s?: string) => {
    if (!s) return '';
    return String(s)
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  };

  // Title rendering: allow responsive wrapping (no hard-limit line breaks)
  // We intentionally avoid truncation so long product names wrap naturally across lines.
  const WHATSAPP_NUMBER = process.env.NEXT_PUBLIC_WHATSAPP_NUMBER || "21698148481";
  const mainProductWhatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Bonjour, je suis intéressé(e) par le produit : ${product.name}.`)}`;

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingReview(true);

    try {
      const formData = new FormData();
      formData.append("productId", product.id);
      formData.append("note", String(formRating));
      formData.append("nom", formName);
      formData.append("etat", "encours");
      formData.append("commentaire", formComment);
      if (formImage) formData.append("image", formImage);

      const apiUrl = `${API_BASE_URL}/reviews`;
      await axios.post(apiUrl, formData);

      setAlert({ type: "success", message: "Merci pour votre avis !" });
      setTimeout(() => setAlert(null), 4000);
      setFormName("");
      setFormComment("");
      setFormImage(null);
      setFormRating(0);
      setReviewModalOpen(false);
    } catch (err: unknown) {
      const maybe = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = maybe?.response?.data?.message || maybe?.message || "Une erreur inconnue est survenue.";
      setAlert({ type: "error", message: "Erreur lors de l'envoi de l'avis : " + errorMessage });
      setTimeout(() => setAlert(null), 5000);
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // videoEmbedUrl not used — removed to avoid unused variable warning

  return (
    <>
      {/* Affichage de l'alerte Tailwind CSS */}
      {alert && (
        <div
          className={`fixed top-6 left-1/2 z-[9999] -translate-x-1/2 px-6 py-3 rounded-full shadow-lg text-white font-semibold transition-all
            ${alert.type === "success" ? "bg-green-600" : "bg-[#343434]"}
          `}
        >
          {alert.message}
        </div>
      )}

      {/* JSON-LD Product schema for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />

      {/* SECTION PRODUIT PRINCIPAL (INCHANGÉ) */}
      <div className="p-3 md:p-6">
        <div id="overview--anchor" className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {/* ... Contenu inchangé ... */}
          {/* Colonne Gauche : Galerie d'images */}
          <ProductGallery product={product} />
          {/* Colonne Droite : Informations Produit */}
          <div className="w-full mt-4 md:mt-0 mb-4">
            <div className="relative">
              <div className="mt-0">
                {/* Product Information */}
                <div className="pt-0 mt-0">
                  <div>
                    {/* New: show "Nouveau" badge when product is recent and the containing category */}

                    {/* Breadcrumbs */}


                    <h1 className="text-2xl font-bold text-center md:text-left py-4">
                      <Link href={`/produit/${encodeURIComponent(product.slug || product.id)}`} className="block w-full no-underline font-bold text-xl md:text-3xl hover:text-[#343434] transition-colors text-slate-900 text-center md:text-left leading-tight whitespace-normal break-words">
                        {product.name}
                      </Link>
                    </h1>

                    {/* Price Information */}
                    <div className='py-1 flex items-baseline gap-3 justify-center md:justify-start'>
                      <span className="font-extrabold text-2xl md:text-3xl lg:text-4xl text-[#343434]">
                        {formatPrice(displayPrice)}
                      </span>
                      {hasDiscount && (
                        <span className="ml-3 text-gray-400 line-through text-sm md:text-base">{formatPrice(product.price)}</span>
                      )}
                      {/* Rating summary */}
                      <div className="ml-3 flex items-center gap-2 text-sm text-gray-600">
                        {totalReviews > 0 ? (
                          <>
                            <Star className="w-4 h-4 text-yellow-400" />
                            <span className="font-semibold">{Number(averageRating).toFixed(1)}</span>
                            <span className="text-gray-400">({totalReviews} avis)</span>
                          </>
                        ) : (
                          <span className="text-gray-400">(Aucun avis)</span>
                        )}
                      </div>
                      {/* Category card + SKU (Block style) */}

                    </div>

                  </div>

                </div>

                {/* Add to Cart Button & Quantity Selector */}
                <div className="mt-2">
                  <div className="flex flex-col md:flex-row items-center gap-3 w-full justify-center md:justify-start">
                    {!isOutOfStock ? (
                      <>
                        <div className="flex items-center w-full md:w-auto gap-2 justify-center md:justify-start">
                          <div className="flex items-center border rounded-lg overflow-hidden bg-white h-10">
                            <button type="button" aria-label="diminuer la quantité" onClick={() => handleQuantityChange('decrease')} className="px-3 text-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                              <Minus className="w-4 h-4 text-[#343434]" />
                            </button>
                            <div className="w-14 text-center font-bold text-gray-800">{quantity}</div>
                            <button type="button" aria-label="augmenter la quantité" onClick={() => handleQuantityChange('increase')} className="px-3 text-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                              <Plus className="w-4 h-4 text-[#343434]" />
                            </button>
                          </div>

                          <button
                            type="button"
                            aria-label={isWished(product.id) ? "Retirer de la liste de souhaits" : "Ajouter à la liste de souhaits"}
                            onClick={() => toggleWish({ id: product.id, name: product.name, price: displayPrice, image: product.images?.[0], slug: product.slug, parentCategoryName: product.category?.name })}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-lg border bg-white text-gray-800 hover:bg-red-50 transition"
                          >
                            <Heart className={`w-5 h-5 ${isWished(product.id) ? 'text-red-500' : 'text-gray-400'}`} />
                          </button>
                        </div>

                        <div className="w-full md:w-auto">
                          <button aria-label="Ajouter au panier" className="text-white bg-main text-sm md:text-base w-full md:w-auto flex items-center justify-center md:justify-start gap-3 rounded-lg border-2 font-bold px-5 py-2 hover:bg-main hover:text-white transition-colors disabled:opacity-50 shadow-sm focus:outline-none" onClick={handleAddToCart} disabled={isAddingToCart}>
                            {isAddingToCart ? (
                              <>
                                <Loader className="w-4 h-4 animate-spin text-white" />
                                <span>Ajout...</span>
                              </>
                            ) : (
                              <>
                                <span>Ajouter au panier</span>
                                <LucideShoppingCart className="w-4 h-4" />
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">

                        <button type="button" onClick={() => openNotifyModal(product.id, product.name)} className="ml-2 inline-flex items-center gap-2 bg-transparent border border-gray-300 text-gray-800 px-3 py-2 rounded-full font-medium hover:bg-gray-50">
                          <Mail className="w-4 h-4 text-gray-700" />
                          Informer
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {/* Summary/Description */}
                {product.description && (
                  <ul className="list-disc pl-5 py-2 text-gray-700 text-base space-y-1 text-left md:text-left">
                    {product.description
                      .split(/\.|\n|\r/)
                      .map(line => line.trim())
                      .filter(line => line.length > 0)
                      .map((line, idx) => (
                        <li key={idx}>{line}</li>
                      ))}
                  </ul>
                )}
                {/* Features List (about_product) */}
                {product.about_product && Array.isArray(product.about_product) && product.about_product.length > 0 && (
                  <div className="my-4 rounded-lg bg-white p-4 md:p-6">
                    <ul className="space-y-3">
                      {product.about_product.map((feature: string, index: number) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="mt-1 flex-shrink-0 text-green-500">
                            <Check className="h-4 w-4" />
                          </span>
                          <div className="text-base font-semibold text-gray-800 text-center md:text-left">{feature}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <VideoStory product={product} />
      {/* Video stories removed */}

      {/* SECTION PRODUITS SIMILAIRES (INCHANGÉ) */}

      {similarProducts.length > 0 && (
        <section id="similair--anchor" className="relative container max-w-6xl mx-auto px-0 py-10">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#343434] mb-8 text-center">
            Produits similaires
          </h2>

          {/* Boutons de navigation */}


          <div className="relative">
            <Swiper
              id="similar-scroll"
              modules={[Pagination]}
              spaceBetween={16}
              slidesPerView={2}
              pagination={{ clickable: true }}
              breakpoints={{
                640: { slidesPerView: 2, spaceBetween: 16 },
                768: { slidesPerView: 3, spaceBetween: 18 },
                1024: { slidesPerView: 4, spaceBetween: 20 },
              }}
              className="!pb-6"
            >
              {similarProducts.map((p) => (
                <SwiperSlide key={p.id} className="overflow-hidden transition-all">
                  <ProductCard product={p} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>


        </section>
      )}

      {/* SECTION AVIS CLIENTS - Modernized UI */}

      <ReviewsSection
        product={product}
        productId={product.id}
        reviews={reviews}
        totalReviews={totalReviews}
      />
      {/* ================================================================== */}
      {/*      BARRE FLOTTANTE / STICKY (EXTRACTED TO COMPONENT)                */}
      {/* ================================================================== */}
      {showBar && (
        <ProductStickyBar
          product={product}
          displayPrice={displayPrice}
          hasDiscount={hasDiscount}
          isOutOfStock={isOutOfStock}
          isAddingToCart={isAddingToCart}
          quantity={quantity}
          stockQuantity={stockQuantity}
          handleQuantityChange={handleQuantityChange}
          handleAddToCart={handleAddToCart}
          openNotifyModal={openNotifyModal}
          mainProductWhatsappUrl={mainProductWhatsappUrl}
          formatPrice={formatPrice}
          navLinks={navLinks}
          activeSection={activeSection}
          onAnchorClick={(id: string) => {
            document.getElementById(`${id}--anchor`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setActiveSection(id);
          }}
          toggleWish={(item) => toggleWish({ ...item, name: item.name ?? '' })}
          isWished={(id) => isWished(id)}
        />
      )}
      {/* MODALE POUR NOTIFICATION - PRODUIT DE RETOUR EN STOCK */}
      {isNotifyModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div onClick={() => setNotifyModalOpen(false)} className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" aria-hidden="true" />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 z-10" onClick={e => e.stopPropagation()}>
            <button onClick={() => setNotifyModalOpen(false)} className="absolute top-3 right-3 p-2 rounded-full text-gray-600 hover:bg-gray-100" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-slate-800">Me prévenir quand le produit est en stock</h3>
            <p className="text-sm text-gray-600 mt-1">Nous vous enverrons un email lorsque <span className="font-medium text-gray-800">{notifyProductName}</span> sera de nouveau disponible.</p>

            <form onSubmit={submitNotify} className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Votre email</label>
              <input value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} type="email" required placeholder="votre@email.com" className="mt-2 w-full rounded-lg border px-3 py-2 shadow-sm text-gray-700 focus:border-red-500 focus:ring-red-500" />
              {notifyAlert && (
                <div className={`mt-3 px-3 py-2 rounded ${notifyAlert.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} role="status">
                  {notifyAlert.message}
                </div>
              )}

              <div className="mt-4 flex justify-end gap-3">
                <button type="button" onClick={() => setNotifyModalOpen(false)} className="px-4 py-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200">Annuler</button>
                <button type="submit" disabled={isSubmittingNotify} className="px-4 py-2 rounded-full bg-[#343434] text-white font-semibold hover:bg-red-700">
                  {isSubmittingNotify ? 'Envoi...' : "M'informer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </>
  );
}

